import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Stack,
  Grid2,
  CircularProgress,
  TextField,
  Button,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Iconify } from '../Minimal/src/components/iconify';
import CommonDatePicker from '../datepicker/CommonDatePicker';
import config from "../common/Config";
import * as XLSX from 'xlsx';
import "../common/commonStyles.css";
import { encryptPayload, normalizeEncryptedApiResponse } from "../../utils/customerCrypto";
import { getCompanySettings, resolveCustomerApiEncrypt } from "../../utils/companySettingsCache";

const CustomerNotOrderReport = () => {
  const [reportrange, setReportrange] = useState('');
  const [dates, setDates] = useState([null, null]);
  const [reportDate, setReportDate] = useState('');
  const [notOrderReport, setNotOrderReport] = useState([]);
  const [totalCustomer, setTotalcustomer] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const accessToken = localStorage.getItem('accessToken');
  const token = localStorage.getItem('token');

  const handleDateChange = (value) => {
    setDates(value);
    if (value && value[0] && value[1]) {
      const formattedStartDate = format(value[0], 'dd/MM/yyyy');
      const formattedEndDate = format(value[1], 'dd/MM/yyyy');
      const newReportRange = formattedStartDate + ' - ' + formattedEndDate;
      setReportrange(newReportRange);
      setReportDate(formattedStartDate + ' To Date : ' + formattedEndDate);
      setPage(0);
    }
  };

  const fetchReport = useCallback(async () => {
    if (!reportrange) return;

    setLoading(true);
    try {
      const settings = await getCompanySettings();
      const useEncrypt = resolveCustomerApiEncrypt(settings);
      const formData = new FormData();
      const plain = {
        draw: 1,
        start: page * rowsPerPage,
        length: rowsPerPage,
        searchValue: searchQuery,
        reportrange
      };
      if (useEncrypt) {
        const ep = encryptPayload(plain);
        if (ep) {
          formData.append('encrypted_payload', ep);
        } else {
          Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
        }
      } else {
        Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
      }

      const response = await fetch(`${config.API_URL}/customer_not_order_report`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to fetch report');

      const raw = await response.json();
      const json = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;

      if (json && json.data && Array.isArray(json.data)) {
        setNotOrderReport(json.data);
        setTotalcustomer(json.recordsTotal || json.data.length);
        if (json.startdate && json.enddate) {
          setReportDate(json.startdate + ' To Date : ' + json.enddate);
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [accessToken, token, page, rowsPerPage, reportrange, searchQuery]);

  useEffect(() => {
    if (reportrange) {
      fetchReport();
    }
  }, [reportrange, page, rowsPerPage, searchQuery, fetchReport]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const exportToExcel = async () => {
    const settings = await getCompanySettings();
    const useEncrypt = resolveCustomerApiEncrypt(settings);
    const formData = new FormData();
    const plain = {
      draw: '1',
      start: '0',
      length: totalCustomer || 10000,
      searchValue: searchQuery,
      reportrange
    };
    if (useEncrypt) {
      const ep = encryptPayload(plain);
      if (ep) {
        formData.append('encrypted_payload', ep);
      } else {
        Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
      }
    } else {
      Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
    }

    const apiActivePdtUrl = `${config.API_URL}/customer_not_order_report`;

    try {
      const response = await fetch(apiActivePdtUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch Products');
      }

      const raw = await response.json();
      const data = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;

      const exportData = (data.data || []).map((product, index) => ({
        "Sr.No": index + 1,
        "Company (A/C No)": product.company || '',
        "Address": product.address || '',
        "Phone": product.phone || '',
        "Post Code": product.postal_code || '',
        "Last Order": product.date || '',
        "Route": product.route || '',
        "Order By": product.order_by || '',
        "Status": product.status === '1' ? 'Active' : 'Inactive'
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Customer not order report');

      const now = new Date();
      const formattedDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const formattedTime = `${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;
      const filename = `customer_not_order_report-${formattedDate}-${formattedTime}.xlsx`;

      XLSX.writeFile(wb, filename);
    } catch (error) {
    }
  };

  return (
    <>
      {/* <Header /> */}
      <Box className="page-container">
        <Paper
          className="paper-style"
        >
          <Box sx={{ px: 2, py: 1.2 }}>
            <Grid2 container spacing={1} >
              <Grid2 size={{ xs: 12, md: 3}}>
              <Typography className="page-title">
                Customer Not Order Report
              </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 4}}>
                <TextField
                  fullWidth
                  className="input-border"
                  placeholder="Search by company, address, phone..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(0);
                  }}
                  size="small"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Iconify icon="eva:search-outline" width={20} sx={{ color: "#0e0f0fef" }} />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSearchQuery('');
                              setPage(0);
                            }}
                            sx={{ color: "#637381" }}
                          >
                            <Iconify icon="eva:close-fill" width={20} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 3}}>
                <CommonDatePicker
                  className="input-border"
                  value={dates}
                  showOneCalendar
                  onChange={handleDateChange}
                  format="dd/MM/yyyy"
                  character=" - "
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md:2}} display="flex" justifyContent="end" alignItems="flex-end">
                <Button
                  variant="outlined"
                  startIcon={<Iconify icon="eva:download-outline" width={20} />}
                  onClick={exportToExcel}
                  className="button-variant-outlined"
                  fullWidth
                >
                  Export to Excel
                </Button>
              </Grid2>
            </Grid2>
            </Box>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={40} />
              </Box>
            ) : (
              <TableContainer >
                <Table size="medium" className="table-cell-border" sx={{ tableLayout: "fixed" }}>
                  <TableHead>
                    <TableRow className="table-cell-header">
                      <TableCell
                        sx={{
                          width: "80px",
                          minWidth: "80px",
                        }}
                      >
                        Sr.No
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "200px",
                          minWidth: "200px",
                        }}
                      >
                        Company (A/C No)
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "250px",
                          minWidth: "250px",
                        }}
                      >
                        Address
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "120px",
                          minWidth: "120px",
                        }}
                      >
                        Phone
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "100px",
                          minWidth: "100px",
                        }}
                      >
                        Post Code
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "120px",
                          minWidth: "120px",
                        }}
                      >
                        Last Order
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "120px",
                          minWidth: "120px",
                        }}
                      >
                        Route
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "120px",
                          minWidth: "120px",
                        }}
                      >
                        Order By
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {notOrderReport.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" sx={{ color: "#637381" }}>
                            {reportrange ? "No data available" : "Please select a date range"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      notOrderReport.map((row, index) => (
                        <TableRow
                          key={index}
                          className="table-cell-style"
                          sx={{
                            "&:last-child td": {
                              borderBottom: "none",
                            }
                          }}
                        >
                          <TableCell
                     
                          >
                            {index + 1 + page * rowsPerPage}
                          </TableCell>
                          <TableCell
                        
                          >
                            {row.company || ""}
                          </TableCell>
                          <TableCell
                    
                          >
                            {row.address || ""}
                          </TableCell>
                          <TableCell
                
                          >
                            {row.phone || ""}
                          </TableCell>
                          <TableCell
                      
                          >
                            {row.postal_code || ""}
                          </TableCell>
                          <TableCell
                        
                          >
                            {row.date || ""}
                          </TableCell>
                          <TableCell
              
                          >
                            {row.route || ""}
                          </TableCell>
                          <TableCell
                 
                          >
                            {row.order_by || ""}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {!loading && notOrderReport.length > 0 && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", borderTop: "1px solid rgba(145, 158, 171, 0.16)" }}>
                <TablePagination
                  component="div"
                  count={totalCustomer}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  labelRowsPerPage="Rows per page:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to}${count !== -1 ? ` of ${count}` : ''}`}
                  className="pagination-styles"
                />
              </Box>
            )}
        
        </Paper>
      </Box>

    </>
  );
};

export default CustomerNotOrderReport;
